import {
  useEncryptWalletRequest,
  useWalletConnection,
  useWatchLocalStorage,
} from '@/hooks';
import {
  clearAuth,
  getStoredAddress,
  isAuthenticated,
  storeAuthToken,
} from '@/libs/auth';
import { apiClient, useApiQuery, useApiMutation } from '@/libs';
import baseImageUrl from '@/utils/baseImageUrl';
import queryKeys from '@/utils/constant/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useAccount, useDisconnect, useSignMessage } from 'wagmi';

export const UserContext = createContext<{
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  getAvatar: () => string;
  refetchConnectWallet: Function;
  canUseApp?: boolean | null;
  setCanUseApp?: React.Dispatch<React.SetStateAction<any>>;
  error?: string | null;
  setError?: React.Dispatch<React.SetStateAction<string | null>>;
}>({
  data: {},
  setData: () => {},
  getAvatar: () => '',
  refetchConnectWallet: () => {},
  error: null,
  setError: () => {},
});

const initialData = { isLoading: null };

export default function UserContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { disconnect } = useDisconnect();
  const { isConnected: isWagmiConnected } = useAccount();
  const [isAuthenticatedState, setIsAuthenticatedState] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pathName = usePathname();
  const queryClient = useQueryClient();
  const { isConnected, address } = useWalletConnection();
  const [localData, setLocalData] = useState<any>(initialData);
  const [canUseApp, setCanUseApp] = useState<any>(null);
  const [subTreeDialog, setSubTreeDialog] = useState<any>(false);
  const token = useWatchLocalStorage('accessToken');

  const walletAddressForConnect = useMemo(
    () => (address ? address : ''),
    [address, pathName]
  );
  const encryptedWallet = useEncryptWalletRequest(walletAddressForConnect);

  const {
    data: connectWalletData,
    refetch: refetchConnectWallet,
    isLoading: isWalletLoading,
  } = useApiQuery(
    ['postUsersConnectWallet', walletAddressForConnect],
    () => {
      if (!walletAddressForConnect || !encryptedWallet) {
        throw new Error('Wallet address or encrypted wallet data is missing.');
      }
      return apiClient.getUsersMe();
    },
    {
      retry: 1,
      enabled: Boolean(walletAddressForConnect && encryptedWallet && !!token),
    }
  );

  const { mutateAsync } = useApiMutation((body: { address: string }) =>
    apiClient.postAuthIdentityWalletNonce(body as any)
  );
  const { signMessageAsync } = useSignMessage();
  const { mutateAsync: verifyMutate } = useApiMutation(
    (body: { address: string; message: string; signature: string }) =>
      apiClient.postAuthIdentityWalletVerify(body)
  );

  const handleDisconnect = useCallback(() => {
    clearAuth();
    setIsAuthenticatedState(false);
    setError(null);
    disconnect();
  }, [disconnect]);

  const combinedData = useMemo(() => {
    if (!isConnected) return { ...localData, token, isLoading: null };

    return {
      ...localData,
      ...(connectWalletData?.data || {}),
      address,
      token,
      isLoading: isWalletLoading || localData.isLoading,
    };
  }, [
    localData,
    connectWalletData?.data,
    address,
    token,
    isConnected,
    isWalletLoading,
  ]);

  const getAvatar = useCallback(() => {
    return combinedData.avatar
      ? baseImageUrl(`public/avatars/${combinedData.avatar}`)
      : '';
  }, [combinedData.avatar]);

  useEffect(() => {
    if (address) {
      const storedAddress = getStoredAddress();
      const authenticated = isAuthenticated() && storedAddress === address;
      setIsAuthenticatedState(authenticated);
    } else {
      setIsAuthenticatedState(false);
    }
  }, [address]);

  useEffect(() => {
    const isAdmin = Boolean(
      typeof window !== 'undefined' && localStorage.getItem('adminLogin')
    );
    if (pathName === '/auth') return;

    if (isAdmin && !token && !!address && !error) {
      setLocalData((prev: any) => ({ ...prev, isLoading: true }));
      if (encryptedWallet) {
        apiClient
          .postUsersConnectWallet(encryptedWallet)
          .then((response) => {
            const resData = (response as any)?.data ?? response;
            const tokenObj = resData?.token;
            if (tokenObj) {
              storeAuthToken(
                tokenObj.accessToken,
                address,
                tokenObj.refreshToken
              );
            }
            const isMaxLeadsActive = Boolean(
              resData?.isActiveInMaxleads ?? resData?.isActiveInMaxLeads
            );

            if (!isMaxLeadsActive) {
              setError(
                'Your account has been suspended by the administrator.\nPlease contact support to restore your access..'
              );
              setCanUseApp?.(false);
              setIsAuthenticatedState(false);
              setLocalData(() => ({
                ...(resData || {}),
                isActiveInMaxleads: false,
                isActiveInMaxLeads: false,
                isLoading: false,
              }));
              return;
            }

            const hasPlan = Boolean(resData?.isActivePlan);
            setCanUseApp?.(hasPlan);
            setIsAuthenticatedState(true);
            setError(null);
            setLocalData((prev: any) => ({ ...prev, isLoading: false }));
          })
          .catch((err) => {
            const serverMsg =
              err?.response?.data?.message ||
              err?.message ||
              'Your account has been suspended by the administrator.\nPlease contact support to restore your access..';
            setError(serverMsg);
            setCanUseApp?.(false);
            setLocalData((prev: any) => ({ ...prev, isLoading: false }));
          });
      }
      return;
    }

    if (!!address && !token && isConnected && !error && !isAdmin) {
      if (!isWagmiConnected) return;

      setLocalData((prev: any) => ({ ...prev, isLoading: true }));
      mutateAsync({ address })
        .then(async (response) => {
          const message = response?.data?.message;
          try {
            const signature = await signMessageAsync({ message });
            const res = await verifyMutate({ address, message, signature });

            const isMaxLeadsActive = Boolean(
              (res?.data as any)?.isActiveInMaxleads ??
                res?.data?.isActiveInMaxLeads ??
                (res as any)?.isActiveInMaxleads ??
                (res as any)?.isActiveInMaxLeads
            );

            const resData = (res as any)?.data ?? res;
            const newToken = resData?.token?.accessToken;
            const newRefreshToken = resData?.token?.refreshToken;

            if (newToken) {
              storeAuthToken(newToken, address, newRefreshToken);
            }

            if (!isMaxLeadsActive) {
              setError('Your account has been suspended by the administrator.\nPlease contact support to restore your access..');
              setCanUseApp?.(false);
              setIsAuthenticatedState(false);
              setLocalData(() => ({
                ...(res?.data || {}),
                isActiveInMaxleads: false,
                isActiveInMaxLeads: false,
                isLoading: false,
              }));
              return;
            }

            const hasPlan = Boolean(res?.data?.isActivePlan);
            setCanUseApp?.(hasPlan);
            setIsAuthenticatedState(true);
            setError(null);
            setLocalData((prev: any) => ({ ...prev, isLoading: false }));
          } catch (err: any) {
            const isUserRejection =
              err?.name === 'UserRejectedRequestError' ||
              err?.code === 4001 ||
              err?.message?.includes('rejected') ||
              err?.message?.includes('User rejected');

            if (isUserRejection) {
              handleDisconnect();
              setLocalData((prev: any) => ({ ...prev, isLoading: false }));
              return;
            }

            const serverMsg =
              err?.response?.data?.message ||
              err?.message ||
              'Your account has been suspended by the administrator.\nPlease contact support to restore your access..';

            clearAuth();
            storeAuthToken('', '');
            setError(serverMsg);
            setCanUseApp?.(false);
            setIsAuthenticatedState(false);
            setLocalData((prev: any) => ({ ...prev, isLoading: false }));
          }
        })
        .catch((err: any) => {
          const isUserRejection =
            err?.name === 'UserRejectedRequestError' ||
            err?.code === 4001 ||
            err?.message?.includes('rejected') ||
            err?.message?.includes('User rejected');

          if (isUserRejection) {
            handleDisconnect();
            setLocalData((prev: any) => ({ ...prev, isLoading: false }));
            return;
          }

          const serverMsg =
            err?.response?.data?.message ||
            err?.message ||
            'Your account has been suspended by the administrator.\nPlease contact support to restore your access..';

          clearAuth();
          storeAuthToken('', '');
          setError(serverMsg);
          setCanUseApp?.(false);
          setIsAuthenticatedState(false);
          setLocalData((prev: any) => ({ ...prev, isLoading: false }));
        });
    }
  }, [
    address,
    token,
    isConnected,
    isWagmiConnected,
    pathName,
    error,
    mutateAsync,
    signMessageAsync,
    verifyMutate,
    handleDisconnect,
    disconnect,
    setCanUseApp,
  ]);

  useEffect(() => {
    if (connectWalletData?.data) {
      const isMaxLeadsActive = Boolean(
        (connectWalletData.data as any)?.isActiveInMaxleads ??
          connectWalletData.data?.isActiveInMaxLeads ??
          (connectWalletData as any)?.isActiveInMaxleads ??
          (connectWalletData as any)?.isActiveInMaxLeads
      );

      if (!isMaxLeadsActive) {
        setError('Your account has been suspended by the administrator.\nPlease contact support to restore your access..');
        setCanUseApp?.(false);
        setIsAuthenticatedState(false);
        setLocalData(() => ({
          ...connectWalletData.data,
          isActiveInMaxleads: false,
          isActiveInMaxLeads: false,
          isLoading: false,
        }));
        return;
      }

      const hasPlan = Boolean(connectWalletData.data.isActivePlan);
      setCanUseApp?.(hasPlan);
      if (hasPlan) {
        setError?.(null);
      }
    } else if (!isConnected && !error) {
      setCanUseApp?.(null);
      setError?.(null);
    }
    if (
      connectWalletData?.data &&
      !connectWalletData.data.isActivePlan &&
      +(connectWalletData.data.wallet?.amount || 0) > 0 &&
      isConnected
    ) {
      setSubTreeDialog(true);
    }
  }, [connectWalletData?.data, isConnected, disconnect, setCanUseApp, setError, error]);

  useEffect(() => {
    if (!isConnected) {
      queryClient.removeQueries([queryKeys.connectWallet] as any);
      setLocalData(initialData);
      setCanUseApp?.(null);
      setError?.(null);
    }
  }, [isConnected, queryClient, setCanUseApp, setError]);

  useEffect(() => {
    if (token && address) refetchConnectWallet();
  }, [address, token, refetchConnectWallet]);
  const contextValue = useMemo(
    () => ({
      data: combinedData,
      setData: setLocalData,
      getAvatar,
      refetchConnectWallet,
      canUseApp,
      setCanUseApp,
      error,
      setError,
    }),
    [combinedData, getAvatar, refetchConnectWallet, canUseApp, error]
  );

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context)
    throw new Error('UserContext must be used within a UserContextProvider');
  return context;
};
